import { format, parseISO } from 'date-fns';

const isValidDateInstance = (value) => value instanceof Date && !Number.isNaN(value.getTime());

const isValidCalendarParts = ({ year, month, day }) => {
  const candidate = new Date(Date.UTC(year, month - 1, day));
  return (
    candidate.getUTCFullYear() === year &&
    candidate.getUTCMonth() === month - 1 &&
    candidate.getUTCDate() === day
  );
};

export const parseCalendarDateParts = (value) => {
  if (value === null || value === undefined || value === '') return null;

  if (isValidDateInstance(value)) {
    return {
      year: value.getUTCFullYear(),
      month: value.getUTCMonth() + 1,
      day: value.getUTCDate(),
    };
  }

  const stringValue = String(value).trim();
  if (!stringValue) return null;

  const isoMatch = stringValue.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s](?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d{1,9})?)?(?:Z|[+-](?:[01]\d|2[0-3])(?::?[0-5]\d)?)?)$/i
  );
  const usMatch = stringValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  let parts = null;

  if (isoMatch) {
    parts = {
      year: Number(isoMatch[1]),
      month: Number(isoMatch[2]),
      day: Number(isoMatch[3]),
    };
  } else if (usMatch) {
    const shortYear = Number(usMatch[3]);
    parts = {
      year: usMatch[3].length === 2 ? shortYear + (shortYear >= 70 ? 1900 : 2000) : shortYear,
      month: Number(usMatch[1]),
      day: Number(usMatch[2]),
    };
  }

  return parts && isValidCalendarParts(parts) ? parts : null;
};

export const formatCalendarDateDisplay = (value, options = {}) => {
  const parts = parseCalendarDateParts(value);
  if (!parts) return options.fallback ?? '';

  const month = String(parts.month).padStart(2, '0');
  const day = String(parts.day).padStart(2, '0');
  return `${month}/${day}/${parts.year}`;
};

export const buildCalendarDateMeta = (value, options = {}) => {
  const parts = parseCalendarDateParts(value);
  if (!parts) {
    return {
      calendar: null,
      display: options.fallback ?? '',
      sort: null,
    };
  }

  const sort = Date.UTC(parts.year, parts.month - 1, parts.day);
  return {
    calendar: parts,
    display: formatCalendarDateDisplay(value, options),
    sort,
  };
};

export const parseFlexibleDate = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (isValidDateInstance(value)) {
    return value;
  }

  if (typeof value === 'number' && !Number.isNaN(value)) {
    const fromNumber = new Date(value);
    return isValidDateInstance(fromNumber) ? fromNumber : null;
  }

  const stringValue = String(value).trim();
  if (!stringValue) {
    return null;
  }

  if (/^\d+$/.test(stringValue)) {
    const numeric = Number(stringValue);
    if (!Number.isNaN(numeric)) {
      const fromNumeric = new Date(numeric);
      if (isValidDateInstance(fromNumeric)) {
        return fromNumeric;
      }
    }
  }

  const candidates = new Set([stringValue]);

  if (stringValue.includes(' ')) {
    candidates.add(stringValue.replace(' ', 'T'));
    candidates.add(stringValue.replace(/ /g, 'T'));
  }

  if (!stringValue.includes('T') && /^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
    candidates.add(`${stringValue}T00:00:00`);
  }

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}Z$/.test(stringValue)) {
    candidates.add(stringValue.replace('Z', ':00Z'));
  }

  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(stringValue)) {
    candidates.add(`${stringValue}:00`);
    candidates.add(`${stringValue}:00Z`);
  }

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(stringValue)) {
    candidates.add(`${stringValue}:00`);
    candidates.add(`${stringValue}:00Z`);
  }

  if (!/[Z+-]$/.test(stringValue)) {
    candidates.add(`${stringValue}Z`);
  }

  for (const candidate of candidates) {
    try {
      const parsed = parseISO(candidate);
      if (isValidDateInstance(parsed)) {
        return parsed;
      }
    } catch (_error) {
      // Ignore parse failures and continue trying.
    }
  }

  const fallback = new Date(stringValue);
  return isValidDateInstance(fallback) ? fallback : null;
};

export const formatDisplayDate = (value, options = {}) => {
  const parsed = isValidDateInstance(value) ? value : parseFlexibleDate(value);
  if (!parsed) {
    return options.fallback ?? '';
  }

  try {
    if (options.timeZone) {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: options.timeZone,
        dateStyle: options.dateStyle ?? 'medium',
        timeStyle: options.timeStyle ?? 'short',
      });
      return formatter.format(parsed);
    }

    return format(parsed, options.format ?? 'Pp');
  } catch (_error) {
    return options.fallback ?? parsed.toLocaleString();
  }
};

export const buildDateMeta = (value, options = {}) => {
  const parsed = parseFlexibleDate(value);
  const display = parsed ? formatDisplayDate(parsed, options) : options.fallback ?? '';
  return {
    parsed,
    display,
    sort: parsed ? parsed.getTime() : null,
  };
};
